import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControlLabel,
    Grid,
    LinearProgress,
    List,
    ListItem,
    ListItemText,
    Paper,
    Stack,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faCircleXmark, faServer, faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { useApplicationContext } from 'ApplicationContext';
import Title from 'Components/Title';
import { LdapDiscoveryResultDto, LdapSettingsDto, LdapTestResultDto, UpdateLdapSettingsRequest } from 'apiClient/Api';
import { LdapGroupRow } from 'Components/LdapGroupPickerDialog';

const WIZARD_STEPS = ['Anmelden', 'Prüfen & speichern', 'Gruppen verknüpfen'];

/**
 * Speicher-Payload inklusive Service-Account-Passwort. Als Schnittmenge deklariert, weil der
 * generierte Client das Feld erst nach einem Server-Neustart plus `npm run generate-api` kennt.
 * `undefined` lässt ein gespeichertes Passwort unverändert, `''` löscht es.
 */
type SaveSettingsPayload = UpdateLdapSettingsRequest & { serviceAccountPassword?: string | null };

/** Leerer Einstellungssatz als Startpunkt für das Formular. */
const emptyForm: UpdateLdapSettingsRequest = {
    enabled: false,
    host: '',
    port: 636,
    useLdaps: true,
    useStartTls: false,
    acceptAnyCertificate: false,
    upnSuffix: '',
    useServiceAccount: false,
    serviceAccountUpn: '',
    baseDn: '',
    groupContainerDn: '',
    connectTimeoutSeconds: 10,
};

const toForm = (dto: LdapSettingsDto): UpdateLdapSettingsRequest => ({
    enabled: dto.enabled ?? false,
    host: dto.host ?? '',
    port: dto.port ?? 636,
    useLdaps: dto.useLdaps ?? true,
    useStartTls: dto.useStartTls ?? false,
    acceptAnyCertificate: dto.acceptAnyCertificate ?? false,
    upnSuffix: dto.upnSuffix ?? '',
    useServiceAccount: dto.useServiceAccount ?? false,
    serviceAccountUpn: dto.serviceAccountUpn ?? '',
    baseDn: dto.baseDn ?? '',
    groupContainerDn: dto.groupContainerDn ?? '',
    connectTimeoutSeconds: dto.connectTimeoutSeconds ?? 10,
});

/** Zeigt die Gruppenliste aus einem Discovery-Ergebnis. */
const GroupList: React.FC<{ groups: LdapGroupRow[]; emptyHint: string }> = ({ groups, emptyHint }) => {
    if (groups.length === 0) {
        return (
            <Typography variant="body2" color="text.secondary">
                {emptyHint}
            </Typography>
        );
    }

    return (
        <Box sx={{ maxHeight: 260, overflowY: 'auto' }}>
            <List dense>
                {groups.map((g) => (
                    <ListItem key={g.dn || g.cn}>
                        <ListItemText
                            primary={
                                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                        {g.cn}
                                    </Typography>
                                    {(g.linkedPermissionGroups?.length ?? 0) > 0 && (
                                        <Chip
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                            label={`verknüpft: ${g.linkedPermissionGroups!.join(', ')}`}
                                        />
                                    )}
                                </Stack>
                            }
                            secondary={g.description || undefined}
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    );
};

/**
 * Einrichtung der AD-/LDAP-Anbindung. Der Assistent kommt mit einem Domain-Login aus und
 * ermittelt Domain-Controller, Port, Verschlüsselung und Verzeichnis-Basis selbst.
 *
 * Die Zuordnung „AD-Gruppe ⇒ Rolle + Berechtigungen" wird nicht hier gepflegt, sondern an der
 * jeweiligen Berechtigungs-Gruppe.
 */
export const LdapSettings: React.FC = () => {
    const { api, apiError, setTitle } = useApplicationContext();
    const { enqueueSnackbar } = useSnackbar();

    const [settings, setSettings] = useState<LdapSettingsDto | null>(null);
    const [form, setForm] = useState<UpdateLdapSettingsRequest>(emptyForm);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    /** Leer = gespeichertes Passwort unverändert lassen. Wird nach dem Speichern zurückgesetzt. */
    const [serviceAccountPassword, setServiceAccountPassword] = useState('');

    // Assistent
    const [wizardOpen, setWizardOpen] = useState(false);
    const [step, setStep] = useState(0);
    const [wizardUpn, setWizardUpn] = useState('');
    const [wizardPassword, setWizardPassword] = useState('');
    const [wizardHost, setWizardHost] = useState('');
    const [discovering, setDiscovering] = useState(false);
    const [discovery, setDiscovery] = useState<LdapDiscoveryResultDto | null>(null);

    // Verbindungstest
    const [testOpen, setTestOpen] = useState(false);
    const [testUpn, setTestUpn] = useState('');
    const [testPassword, setTestPassword] = useState('');
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState<LdapTestResultDto | null>(null);

    useEffect(() => setTitle('AD-Anbindung'), [setTitle]);

    const reload = useCallback(() => {
        setLoading(true);
        api.ldapAdmin
            .ldapAdminGetSettings()
            .then((r) => {
                setSettings(r.data);
                setForm(toForm(r.data));
            })
            .catch(apiError)
            .finally(() => setLoading(false));
    }, [api, apiError]);

    useEffect(() => reload(), [reload]);

    const patch = (changes: Partial<UpdateLdapSettingsRequest>) => setForm((prev) => ({ ...prev, ...changes }));

    const save = async (values: UpdateLdapSettingsRequest, password?: string) => {
        setSaving(true);
        try {
            const payload: SaveSettingsPayload = { ...values };
            // Nur mitschicken, wenn tatsächlich etwas eingegeben wurde — sonst bleibt das
            // gespeicherte Passwort unangetastet.
            if (password !== undefined) payload.serviceAccountPassword = password;
            await api.ldapAdmin.ldapAdminSaveSettings(payload);
            enqueueSnackbar('Einstellungen gespeichert — sie greifen ohne Neustart.', { variant: 'success' });
            setServiceAccountPassword('');
            reload();
            return true;
        } catch (e) {
            apiError(e);
            return false;
        } finally {
            setSaving(false);
        }
    };

    const runDiscovery = async () => {
        setDiscovering(true);
        setDiscovery(null);
        try {
            const response = await api.ldapAdmin.ldapAdminDiscover({
                upn: wizardUpn.trim(),
                password: wizardPassword,
                host: wizardHost.trim() || null,
            });
            setDiscovery(response.data);
            if (response.data.success && response.data.proposal) {
                setForm(response.data.proposal);
                setStep(1);
            }
        } catch (e) {
            apiError(e);
        } finally {
            setDiscovering(false);
        }
    };

    const acceptProposal = async () => {
        if (await save(form, serviceAccountPassword || undefined)) setStep(2);
    };

    const closeWizard = () => {
        setWizardOpen(false);
        setStep(0);
        setWizardPassword('');
        setDiscovery(null);
    };

    const runTest = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const response = await api.ldapAdmin.ldapAdminTestConnection({
                upn: testUpn.trim() || null,
                password: testPassword || null,
            });
            setTestResult(response.data);
        } catch (e) {
            apiError(e);
        } finally {
            setTesting(false);
        }
    };

    const active = (settings?.enabled ?? false) && (settings?.isComplete ?? false);

    return (
        <Grid size={12}>
            <Title>AD-Anbindung</Title>
            {loading && <LinearProgress sx={{ mb: 2 }} />}

            <Grid container spacing={2}>
                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Stack
                            direction={{ xs: 'column', md: 'row' }}
                            spacing={2}
                            sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}
                        >
                            <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                                <FontAwesomeIcon icon={active ? faCircleCheck : faCircleXmark} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {active ? 'AD-Login aktiv' : 'AD-Login inaktiv'}
                                </Typography>
                                {settings?.enabled && !settings?.isComplete && (
                                    <Chip size="small" color="warning" label="unvollständig konfiguriert" />
                                )}
                                {settings?.host && <Chip size="small" variant="outlined" label={settings.host} />}
                                {settings?.useServiceAccount && <Chip size="small" variant="outlined" label="Service-Account" />}
                            </Stack>
                            <Stack direction="row" spacing={1}>
                                <Button
                                    variant="contained"
                                    startIcon={<FontAwesomeIcon icon={faWandMagicSparkles} />}
                                    onClick={() => setWizardOpen(true)}
                                >
                                    Einrichtungs-Assistent
                                </Button>
                                <Button
                                    variant="outlined"
                                    startIcon={<FontAwesomeIcon icon={faServer} />}
                                    onClick={() => {
                                        setTestResult(null);
                                        setTestOpen(true);
                                    }}
                                    disabled={!settings?.host}
                                >
                                    Verbindung testen
                                </Button>
                            </Stack>
                        </Stack>

                        {settings?.enabled && !settings?.isComplete && (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                Der AD-Login ist eingeschaltet, aber es fehlen Pflichtangaben (Host, Verzeichnis-Basis
                                oder — bei Service-Account-Betrieb — die Anmeldedaten des Service-Accounts). Bis dahin
                                bleibt er wirkungslos.
                            </Alert>
                        )}

                        {!settings?.useServiceAccount && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Angemeldet wird als der jeweilige Benutzer selbst — es ist kein Service-Account nötig
                                und es wird nirgends ein AD-Passwort gespeichert.
                            </Alert>
                        )}
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Verbindung
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Normalerweise füllt der Assistent diese Felder. Manuelle Änderungen greifen sofort, ohne
                            Server-Neustart.
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Domain-Controller (FQDN)"
                                    placeholder="dc01.firma.local"
                                    value={form.host ?? ''}
                                    onChange={(e) => patch({ host: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    label="Port"
                                    value={form.port ?? 636}
                                    onChange={(e) => patch({ port: Number(e.target.value) })}
                                />
                            </Grid>
                            <Grid size={{ xs: 6, md: 3 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    type="number"
                                    label="Timeout (Sek.)"
                                    value={form.connectTimeoutSeconds ?? 10}
                                    onChange={(e) => patch({ connectTimeoutSeconds: Number(e.target.value) })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Verzeichnis-Basis (BaseDn)"
                                    placeholder="DC=firma,DC=local"
                                    value={form.baseDn ?? ''}
                                    onChange={(e) => patch({ baseDn: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="Gruppen-Container (optional)"
                                    placeholder="OU=Groups,DC=firma,DC=local"
                                    helperText="Leer = gesamte Verzeichnis-Basis durchsuchen"
                                    value={form.groupContainerDn ?? ''}
                                    onChange={(e) => patch({ groupContainerDn: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <TextField
                                    size="small"
                                    fullWidth
                                    label="UPN-Suffix"
                                    placeholder="firma.local"
                                    helperText="Wird angehängt, wenn jemand nur den Benutzernamen ohne @ eingibt"
                                    value={form.upnSuffix ?? ''}
                                    onChange={(e) => patch({ upnSuffix: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Stack spacing={0.5}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={form.useLdaps ?? false}
                                                onChange={(e) => patch({ useLdaps: e.target.checked, useStartTls: false })}
                                            />
                                        }
                                        label="LDAPS (TLS direkt, Port 636)"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={form.useStartTls ?? false}
                                                onChange={(e) => patch({ useStartTls: e.target.checked, useLdaps: false })}
                                            />
                                        }
                                        label="StartTLS (Port 389)"
                                    />
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={form.acceptAnyCertificate ?? false}
                                                onChange={(e) => patch({ acceptAnyCertificate: e.target.checked })}
                                            />
                                        }
                                        label="Zertifikat nicht prüfen (nur Test/Entwicklung)"
                                    />
                                </Stack>
                            </Grid>
                        </Grid>

                        {form.acceptAnyCertificate && (
                            <Alert severity="warning" sx={{ mt: 1 }}>
                                Unter Linux greift diese Option möglicherweise nicht — die Zertifikatsprüfung läuft dort
                                über OpenLDAP (<code>/etc/ldap/ldap.conf</code> bzw. <code>LDAPTLS_REQCERT</code>).
                                Sauberer ist es, die interne CA in den System-Trust-Store des Servers zu legen.
                            </Alert>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={form.useServiceAccount ?? false}
                                    onChange={(e) => patch({ useServiceAccount: e.target.checked })}
                                />
                            }
                            label="Suchen über einen Service-Account (nur nötig, wenn das AD normalen Benutzern das Lesen von Gruppen verweigert)"
                        />
                        {form.useServiceAccount && (
                            <Stack spacing={1.5} sx={{ mt: 1 }}>
                                <TextField
                                    size="small"
                                    label="Service-Account (UPN)"
                                    placeholder="svc-support@firma.local"
                                    value={form.serviceAccountUpn ?? ''}
                                    onChange={(e) => patch({ serviceAccountUpn: e.target.value })}
                                />
                                <TextField
                                    size="small"
                                    type="password"
                                    label="Passwort des Service-Accounts"
                                    placeholder={
                                        settings?.serviceAccountPasswordConfigured
                                            ? 'gespeichert — nur ausfüllen, um es zu ersetzen'
                                            : 'Passwort eingeben'
                                    }
                                    value={serviceAccountPassword}
                                    onChange={(e) => setServiceAccountPassword(e.target.value)}
                                    helperText="Wird verschlüsselt auf dem Server gespeichert und nie wieder ausgeliefert."
                                    autoComplete="new-password"
                                />
                                <Alert severity={settings?.serviceAccountPasswordConfigured ? 'success' : 'warning'}>
                                    {settings?.serviceAccountPasswordConfigured
                                        ? 'Ein Passwort ist gespeichert. Das Feld leer lassen, um es unverändert zu übernehmen.'
                                        : 'Es ist noch kein Passwort gespeichert — ohne Passwort lässt sich der Service-Account-Betrieb nicht aktivieren.'}
                                </Alert>
                            </Stack>
                        )}

                        <Divider sx={{ my: 2 }} />

                        <Stack direction="row" spacing={2} sx={{ alignItems: 'center', flexWrap: 'wrap' }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.enabled ?? false}
                                        onChange={(e) => patch({ enabled: e.target.checked })}
                                    />
                                }
                                label="AD-Login aktiv"
                            />
                            <Button
                                variant="contained"
                                onClick={() => void save(form, serviceAccountPassword || undefined)}
                                disabled={saving}
                            >
                                Speichern
                            </Button>
                            <Button
                                onClick={() => {
                                    setServiceAccountPassword('');
                                    reload();
                                }}
                                disabled={saving}
                            >
                                Verwerfen
                            </Button>
                        </Stack>
                    </Paper>
                </Grid>

                <Grid size={12}>
                    <Alert severity="info">
                        <AlertTitle>Wo werden Rollen zugeordnet?</AlertTitle>
                        Nicht hier. Eine AD-Gruppe wird an der jeweiligen Berechtigungs-Gruppe verknüpft — dort legst du
                        fest, welche Rolle sie vergibt und welche Berechtigungen dazukommen. Zu finden unter{' '}
                        <RouterLink to="/admin/permissions">Berechtigungen</RouterLink>.
                    </Alert>
                </Grid>
            </Grid>

            {/* ---------------- Assistent ---------------- */}
            <Dialog open={wizardOpen} onClose={closeWizard} maxWidth="md" fullWidth>
                <DialogTitle>Einrichtungs-Assistent</DialogTitle>
                <DialogContent>
                    <Stepper activeStep={step} sx={{ mb: 3, mt: 1 }}>
                        {WIZARD_STEPS.map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>

                    {step === 0 && (
                        <Stack spacing={2}>
                            <Typography variant="body2" color="text.secondary">
                                Ein Domain-Login genügt. Daraus werden Domain-Controller, Port, Verschlüsselung und
                                Verzeichnis-Basis ermittelt. Die Anmeldedaten werden nur für diese Suche verwendet und
                                nicht gespeichert.
                            </Typography>
                            <TextField
                                size="small"
                                label="Anmeldename"
                                placeholder="vorname.nachname@firma.local"
                                autoFocus
                                value={wizardUpn}
                                onChange={(e) => setWizardUpn(e.target.value)}
                            />
                            <TextField
                                size="small"
                                type="password"
                                label="Passwort"
                                value={wizardPassword}
                                onChange={(e) => setWizardPassword(e.target.value)}
                            />
                            <TextField
                                size="small"
                                label="Domain-Controller (nur falls die Domain nicht per DNS auflösbar ist)"
                                placeholder="optional"
                                value={wizardHost}
                                onChange={(e) => setWizardHost(e.target.value)}
                            />
                            {discovering && <LinearProgress />}
                            {discovery && !discovery.success && (
                                <Alert severity="error">
                                    <AlertTitle>Nicht erfolgreich</AlertTitle>
                                    {discovery.message}
                                </Alert>
                            )}
                        </Stack>
                    )}

                    {step === 1 && discovery && (
                        <Stack spacing={2}>
                            <Alert severity="success">{discovery.message}</Alert>
                            {(discovery.warnings ?? []).map((w, i) => (
                                <Alert key={i} severity="warning">
                                    {w}
                                </Alert>
                            ))}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Domain-Controller"
                                        value={form.host ?? ''}
                                        onChange={(e) => patch({ host: e.target.value })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        type="number"
                                        label="Port"
                                        value={form.port ?? 636}
                                        onChange={(e) => patch({ port: Number(e.target.value) })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 6, md: 3 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Verschlüsselung"
                                        value={form.useLdaps ? 'LDAPS' : form.useStartTls ? 'StartTLS' : 'keine'}
                                        disabled
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="Verzeichnis-Basis"
                                        value={form.baseDn ?? ''}
                                        onChange={(e) => patch({ baseDn: e.target.value })}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        size="small"
                                        fullWidth
                                        label="UPN-Suffix"
                                        value={form.upnSuffix ?? ''}
                                        onChange={(e) => patch({ upnSuffix: e.target.value })}
                                    />
                                </Grid>
                            </Grid>
                            <Divider />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.useServiceAccount ?? false}
                                        onChange={(e) => patch({ useServiceAccount: e.target.checked })}
                                    />
                                }
                                label="Verbindung über einen Service-Account führen (empfohlen)"
                            />
                            {form.useServiceAccount && (
                                <Stack spacing={1.5}>
                                    <Typography variant="body2" color="text.secondary">
                                        Damit laufen alle Verzeichnisabfragen über ein Dienstkonto statt über die
                                        Verbindung des jeweiligen Benutzers. Das Passwort wird verschlüsselt auf dem
                                        Server gespeichert. Ein Lesekonto genügt.
                                    </Typography>
                                    <TextField
                                        size="small"
                                        label="Service-Account (UPN)"
                                        placeholder="svc-support@firma.local"
                                        value={form.serviceAccountUpn ?? ''}
                                        onChange={(e) => patch({ serviceAccountUpn: e.target.value })}
                                    />
                                    <TextField
                                        size="small"
                                        type="password"
                                        label="Passwort des Service-Accounts"
                                        value={serviceAccountPassword}
                                        onChange={(e) => setServiceAccountPassword(e.target.value)}
                                        autoComplete="new-password"
                                    />
                                </Stack>
                            )}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.enabled ?? false}
                                        onChange={(e) => patch({ enabled: e.target.checked })}
                                    />
                                }
                                label="AD-Login gleich aktivieren"
                            />
                        </Stack>
                    )}

                    {step === 2 && discovery && (
                        <Stack spacing={2}>
                            <Alert severity="success">
                                Gespeichert. Jetzt fehlt nur noch die Zuordnung der Gruppen.
                            </Alert>
                            <Alert severity="info">
                                <AlertTitle>Nächster Schritt</AlertTitle>
                                Lege unter <RouterLink to="/admin/permissions">Berechtigungen</RouterLink> pro AD-Gruppe
                                eine Berechtigungs-Gruppe an (oder verknüpfe eine bestehende) und wähle dort die Rolle.
                                <strong> Mindestens eine Gruppe muss eine Rolle vergeben</strong>, sonst wird jeder
                                Login abgelehnt.
                            </Alert>
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Deine eigenen Gruppen ({discovery.yourGroups?.length ?? 0})
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Die naheliegendsten Kandidaten — hier bist du selbst Mitglied.
                                </Typography>
                                <GroupList
                                    groups={(discovery.yourGroups ?? []) as LdapGroupRow[]}
                                    emptyHint="Keine eigenen Gruppen gefunden."
                                />
                            </Box>
                            <Divider />
                            <Box>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    Alle Gruppen im Verzeichnis ({discovery.allGroups?.length ?? 0})
                                </Typography>
                                <GroupList
                                    groups={(discovery.allGroups ?? []) as LdapGroupRow[]}
                                    emptyHint="Keine Gruppen gefunden."
                                />
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeWizard}>{step === 2 ? 'Fertig' : 'Abbrechen'}</Button>
                    {step === 0 && (
                        <Button
                            variant="contained"
                            onClick={() => void runDiscovery()}
                            disabled={discovering || !wizardUpn.includes('@') || !wizardPassword}
                        >
                            Verzeichnis suchen
                        </Button>
                    )}
                    {step === 1 && (
                        <Button variant="contained" onClick={() => void acceptProposal()} disabled={saving}>
                            Übernehmen und speichern
                        </Button>
                    )}
                    {step === 2 && (
                        <Button variant="contained" component={RouterLink} to="/admin/permissions">
                            Zu den Berechtigungen
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* ---------------- Verbindungstest ---------------- */}
            <Dialog open={testOpen} onClose={() => setTestOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Verbindung testen</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            Getestet werden die <strong>gespeicherten</strong> Einstellungen.
                        </Typography>
                        {settings?.serviceAccountPasswordConfigured ? (
                            <Alert severity="info">
                                Getestet wird über den konfigurierten Service-Account — es ist keine persönliche
                                Anmeldung nötig.
                            </Alert>
                        ) : (
                            <>
                                <Alert severity="info">
                                    Es ist kein Service-Account konfiguriert, daher wird für den Test eine persönliche
                                    Anmeldung benötigt. Sie wird nicht gespeichert.
                                </Alert>
                                <TextField
                                    size="small"
                                    label="Anmeldename"
                                    placeholder="vorname.nachname@firma.local"
                                    value={testUpn}
                                    onChange={(e) => setTestUpn(e.target.value)}
                                />
                                <TextField
                                    size="small"
                                    type="password"
                                    label="Passwort"
                                    value={testPassword}
                                    onChange={(e) => setTestPassword(e.target.value)}
                                />
                            </>
                        )}
                        {testing && <LinearProgress />}
                        {testResult && (
                            <Alert severity={testResult.success ? 'success' : 'error'}>
                                <AlertTitle>
                                    {testResult.success ? 'Erfolgreich' : 'Fehlgeschlagen'}
                                    {typeof testResult.elapsedMilliseconds === 'number' &&
                                        ` (${testResult.elapsedMilliseconds} ms)`}
                                </AlertTitle>
                                {testResult.message}
                            </Alert>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setTestOpen(false)}>Schließen</Button>
                    <Button variant="contained" onClick={() => void runTest()} disabled={testing}>
                        Testen
                    </Button>
                </DialogActions>
            </Dialog>
        </Grid>
    );
};

export default LdapSettings;
